import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Trophy, DollarSign, Clock, Target, TrendingUp } from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

const RANGES = [
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'qtd', label: 'This quarter', days: null },
  { key: 'ytd', label: 'Year to date', days: null }
];

function rangeToDates(key) {
  const now = new Date();
  const to = new Date(now);
  let from;
  if (key === '30d') from = new Date(now.getTime() - 30 * 86400000);
  else if (key === '90d') from = new Date(now.getTime() - 90 * 86400000);
  else if (key === 'qtd') {
    const q = Math.floor(now.getMonth() / 3);
    from = new Date(now.getFullYear(), q * 3, 1);
  } else if (key === 'ytd') {
    from = new Date(now.getFullYear(), 0, 1);
  } else {
    from = new Date(now.getTime() - 30 * 86400000);
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

const SummaryCard = ({ icon: Icon, label, value, sub, tint = 'blue' }) => {
  const tintMap = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700',
    indigo: 'bg-indigo-50 text-indigo-700'
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tintMap[tint]}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
};

const SalesPerformanceReport = () => {
  const [rangeKey, setRangeKey] = useState('30d');
  const { from, to } = useMemo(() => rangeToDates(rangeKey), [rangeKey]);

  const { data, isLoading, error } = useQuery(
    ['reports-sales', rangeKey],
    () => api.get('/reports/sales', { params: { from, to } }).then((r) => r.data)
  );

  if (error) {
    return <div className="text-red-600 text-sm p-4">Failed to load sales report.</div>;
  }

  const funnel = data?.funnel || [];
  const openFunnel = funnel.filter((s) => !s.isClosed);
  const closedFunnel = funnel.filter((s) => s.isClosed);
  const maxOpenCount = Math.max(1, ...openFunnel.map((s) => s.count));
  const winRate = data?.winRate || { won: 0, lost: 0, rate: 0, totalRevenue: 0, avgDealSize: 0 };
  const cycleLength = data?.cycleLength || { wonCount: 0, avgDays: 0 };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp size={22} /> Sales Performance
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Pipeline funnel, win rate and cycle length for the selected period.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${rangeKey === r.key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <SummaryCard
              icon={Trophy}
              tint="green"
              label="Deals Won"
              value={winRate.won}
              sub={winRate.lost > 0 ? `${winRate.lost} lost` : 'no losses'}
            />
            <SummaryCard
              icon={DollarSign}
              tint="indigo"
              label="Won Revenue"
              value={formatCurrency(winRate.totalRevenue, data?.currency || 'USD')}
              sub={`avg ${formatCurrency(winRate.avgDealSize, data?.currency || 'USD')} per deal`}
            />
            <SummaryCard
              icon={Target}
              tint="blue"
              label="Win Rate"
              value={`${winRate.rate}%`}
              sub={`${winRate.won + winRate.lost} decided`}
            />
            <SummaryCard
              icon={Clock}
              tint="amber"
              label="Avg Cycle"
              value={cycleLength.avgDays ? `${cycleLength.avgDays}d` : '—'}
              sub={cycleLength.wonCount > 0 ? `from ${cycleLength.wonCount} won leads` : 'no wins yet'}
            />
          </div>

          {/* Funnel */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Pipeline Funnel (current snapshot)</h3>
            {openFunnel.length === 0 ? (
              <p className="text-gray-400 text-sm italic">No stages configured.</p>
            ) : (
              <div className="space-y-2.5">
                {openFunnel.map((s, i) => {
                  const pct = (s.count / maxOpenCount) * 100;
                  return (
                    <div key={s.stageId} className="flex items-center gap-3">
                      <div className="w-32 text-xs text-gray-700 flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name}
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-md overflow-hidden h-6 relative">
                        <div
                          className="h-full rounded-md transition-all"
                          style={{ width: `${pct}%`, backgroundColor: s.color + 'cc' }}
                        />
                        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs font-medium">
                          <span className="text-gray-900">{s.count} lead{s.count !== 1 ? 's' : ''}</span>
                          <span className="text-gray-700">{formatCurrency(s.totalValue, 'USD')}</span>
                        </div>
                      </div>
                      <div className="w-20 text-[11px] text-gray-500 text-right">
                        {i > 0 && s.conversionFromPrev !== undefined ? `${s.conversionFromPrev}% ↓` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Closed stages + cycle detail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Closed deals</h3>
              <div className="space-y-2">
                {closedFunnel.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">No closed stages.</p>
                ) : closedFunnel.map((s) => (
                  <div key={s.stageId} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-sm text-gray-700">{s.name}</span>
                    </div>
                    <div className="text-sm text-gray-900 font-medium">
                      {s.count} · {formatCurrency(s.totalValue, 'USD')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Stage stagnation</h3>
              <p className="text-xs text-gray-500 mb-3">
                Average days leads have been sitting in each open stage.
              </p>
              <div className="space-y-1.5">
                {openFunnel.map((s) => (
                  <div key={s.stageId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}
                    </div>
                    <span className={`font-medium ${s.avgDaysInStage > 30 ? 'text-red-600' : s.avgDaysInStage > 14 ? 'text-amber-600' : 'text-gray-900'}`}>
                      {s.avgDaysInStage}d
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesPerformanceReport;
